import { z } from 'zod'

/**
 * Media vocabulary (decision D9).
 *
 * Note what is NOT here: any provider name, account, or URL shape. The client
 * addresses every asset through a first-party path built by `mediaUrl()`, and the
 * server resolves it. That is the whole reason swapping Cloudinary for VPS storage
 * later is an adapter change rather than a data migration
 * (docs/02-architecture.md §11).
 */

export const MEDIA_VARIANTS = ['thumb', 'card', 'full', 'original'] as const
export type MediaVariant = (typeof MEDIA_VARIANTS)[number]
export const mediaVariantSchema = z.enum(MEDIA_VARIANTS)

/**
 * Provider-neutral rendition specs. The adapter translates these into whatever its
 * provider speaks — Cloudinary transformation segments today, an image-resizing
 * service or a pre-generated file tomorrow.
 */
export interface VariantSpec {
  width?: number
  height?: number
  fit?: 'cover' | 'contain' | 'inside'
}

export const VARIANT_SPECS: Record<MediaVariant, VariantSpec> = {
  thumb: { width: 96, height: 96, fit: 'cover' },
  card: { width: 480, height: 480, fit: 'contain' },
  full: { width: 1400, fit: 'inside' },
  original: {},
}

export const MEDIA_ACCESS = ['public', 'private'] as const
export type MediaAccess = (typeof MEDIA_ACCESS)[number]
export const mediaAccessSchema = z.enum(MEDIA_ACCESS)

export const MEDIA_KINDS = ['image', 'document'] as const
export type MediaKind = (typeof MEDIA_KINDS)[number]
export const mediaKindSchema = z.enum(MEDIA_KINDS)

export const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'] as const
export const DOCUMENT_MIME_TYPES = ['application/pdf'] as const

export const MAX_IMAGE_BYTES = 8 * 1024 * 1024
export const MAX_DOCUMENT_BYTES = 15 * 1024 * 1024

export function allowedMimeTypes(kind: MediaKind): readonly string[] {
  return kind === 'image' ? IMAGE_MIME_TYPES : DOCUMENT_MIME_TYPES
}

export function maxBytesFor(kind: MediaKind): number {
  return kind === 'image' ? MAX_IMAGE_BYTES : MAX_DOCUMENT_BYTES
}

/** What an asset is attached to. Drives both the folder layout and the access check. */
export const MEDIA_PURPOSES = [
  'product_image',
  'org_logo',
  'listing_image',
  'proposal_image',
  'chat_attachment',
] as const
export type MediaPurpose = (typeof MEDIA_PURPOSES)[number]
export const mediaPurposeSchema = z.enum(MEDIA_PURPOSES)

/** Chat attachments are private; catalog imagery is public and edge-cached. */
export const PURPOSE_ACCESS: Record<MediaPurpose, MediaAccess> = {
  product_image: 'public',
  org_logo: 'public',
  listing_image: 'public',
  proposal_image: 'public',
  chat_attachment: 'private',
}

export const assetSchema = z.object({
  id: z.string(),
  purpose: mediaPurposeSchema,
  kind: mediaKindSchema,
  access: mediaAccessSchema,
  status: z.enum(['pending', 'ready', 'failed']),
  fileName: z.string().nullable(),
  contentType: z.string().nullable(),
  bytes: z.number().int().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  /** Tiny blurred placeholder, inlined as a data URI to avoid a second request. */
  blurhash: z.string().nullable(),
  createdAt: z.number().int(),
})
export type Asset = z.infer<typeof assetSchema>

export const requestUploadSchema = z.object({
  purpose: mediaPurposeSchema,
  fileName: z.string().trim().min(1).max(200),
  contentType: z.string().trim().min(3).max(100),
  bytes: z.number().int().min(1),
  /** The row this asset will hang off, when it already exists. */
  ownerId: z.string().max(64).nullish(),
})
export type RequestUploadInput = z.infer<typeof requestUploadSchema>

export const uploadTicketSchema = z.object({
  assetId: z.string(),
  uploadUrl: z.string(),
  /** Multipart form fields to send alongside the file, verbatim. */
  fields: z.record(z.string(), z.string()),
  expiresAt: z.number().int(),
  maxBytes: z.number().int(),
})
export type UploadTicket = z.infer<typeof uploadTicketSchema>

/**
 * The only way the client should ever build an image URL.
 * Variants are immutable, so these are safe to cache forever at the edge.
 */
export function mediaUrl(assetId: string, variant: MediaVariant = 'card'): string {
  return `/media/${assetId}/${variant}`
}

/** `srcset` for responsive images, using the variants we actually generate. */
export function mediaSrcSet(assetId: string): string {
  return [
    `${mediaUrl(assetId, 'thumb')} 96w`,
    `${mediaUrl(assetId, 'card')} 480w`,
    `${mediaUrl(assetId, 'full')} 1400w`,
  ].join(', ')
}
